import React from 'react';
import { RiInformationFill } from "react-icons/ri";
import ColumnConfigurationTable from './ColumnConfigurationTable';
import { FileAnalysis } from '../../types';
import { getButtonClasses, getButtonStyles } from '../../constants';
import { BackendDQRule } from '../../types/dqRules';
import { useTranslate } from '../../hooks/useTranslate';

interface DataObjectConfigProps {
    // Common props
    dataObjectName: string;
    dataObjectDescription: string;
    nameError: string;
    onNameChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    nameInputRef: React.RefObject<HTMLInputElement>;
    descriptionRef: React.RefObject<HTMLTextAreaElement>;
    lastUserFocusedRef: React.MutableRefObject<HTMLElement | null>;
    // Optional identifier propagated by some callers; not used internally
    dataObjectId?: string;

    // Table data
    analysis: FileAnalysis;
    onColumnUpdate: (columnName: string, key: string, value: any) => void;
    // Optional: Databricks table name provided by some callers
    tableName?: string;
    // Rules data for editing existing data objects
    existingRules?: BackendDQRule[];
    rulesLoading?: boolean;

    // Column selection for scoped reporting
    onSelectionChange?: (selectedColumns: string[]) => void;
    initialSelectedColumns?: string[];

    // Footer actions
    isValid: boolean;
    isProcessing: boolean;
    onSubmit: () => void;
    submitLabel: string;
    onReset?: () => void;
    resetLabel?: string;

    // Optional source info (for Databricks)
    sourceInfo?: string;

    // Control footer visibility
    showFooter?: boolean;
    // Column names to highlight as NEW (green border + badge)
    newColumnNames?: string[];
    // Hide the name/description header section
    hideHeader?: boolean;
}

const DataObjectConfig: React.FC<DataObjectConfigProps> = ({
    dataObjectName,
    dataObjectDescription,
    nameError,
    onNameChange,
    onDescriptionChange,
    nameInputRef,
    descriptionRef,
    lastUserFocusedRef,
    dataObjectId,
    analysis,
    onColumnUpdate,
    isValid,
    isProcessing,
    onSubmit,
    submitLabel,
    onReset,
    resetLabel,
    sourceInfo,
    showFooter = true,
    tableName,
    existingRules,
    rulesLoading,
    onSelectionChange,
    initialSelectedColumns,
    newColumnNames,
    hideHeader = false
}) => {
    const { t } = useTranslate();
    return (
        <div className="flex flex-col h-full">
            {/* Header form - responsive 2-column layout */}
            {/* AI Disclaimer - Highlighted and Bold */}
            <div className="px-6 mt-2 mb-2">
                <div className="inline-block p-2 bg-red-50 border-l-4 border-red-500 rounded-r-lg shadow-sm">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            aria-label="Information"
                            className="flex items-center justify-center w-5 h-5 rounded bg-white/70 hover:bg-white text-red-700 shadow-sm border border-red-200"
                        >
                            <RiInformationFill className="w-3 h-3" />
                        </button>
                        <span className="font-bold text-red-700 text-xs">{t('Object and Column Description are AI generated. Please review for accuracy.')}</span>
                    </div>
                </div>
            </div>

            {!hideHeader && (
                <div className="flex-shrink-0 px-6 py-2 border-b border-gray-200 bg-gradient-to-r from-red-50/50 to-orange-50/30">

                    {sourceInfo && (
                        <div className="mb-4 p-3 bg-gray-100 border border-gray-300 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">{t('Source Table:')} </span>
                            <span className="text-sm font-mono text-gray-900 bg-gray-200 px-2 py-1 rounded">{sourceInfo}</span>
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
                        {/* Object Name */}
                        <div>
                            <label htmlFor="dataObjectName" className="block text-sm font-semibold text-red-700 mb-2">
                                {t('Object Name')}
                            </label>
                            <input
                                ref={nameInputRef}
                                type="text"
                                id="dataObjectName"
                                value={dataObjectName}
                                onFocus={(e) => { lastUserFocusedRef.current = e.currentTarget }}
                                onChange={(e) => onNameChange(e.target.value)}
                                aria-invalid={!!nameError}
                                aria-describedby={nameError ? 'dataObjectName-error' : undefined}
                                className={`w-full px-3 py-2 lg:py-2 text-sm lg:text-base border-2 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-300 bg-white text-gray-900 shadow-sm hover:shadow-md ${nameError ? 'border-red-500 bg-red-50' : 'border-red-300 hover:border-red-400'}`}
                                placeholder="Enter descriptive object name"
                            />
                            {nameError && (
                                <p id="dataObjectName-error" className="mt-1 text-xs text-red-600 flex items-center" role="alert">
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    {nameError}
                                </p>
                            )}
                        </div>

                        {/* Object Description */}
                        <div>
                            <label htmlFor="dataObjectDescription" className="block text-sm font-semibold text-red-700 mb-2">
                                <span className="inline-flex items-center gap-2">
                                    <button
                                        type="button"
                                        aria-label="Information"
                                        className="flex items-center justify-center w-6 h-6 rounded bg-white/70 hover:bg-white text-red-700 shadow-sm border border-red-200"
                                    >
                                        <RiInformationFill className="w-4 h-4" />
                                    </button>
                                    <span>{t('Object Description')}</span>
                                </span>
                            </label>
                            <textarea
                                id="dataObjectDescription"
                                ref={descriptionRef}
                                value={dataObjectDescription}
                                onFocus={(e) => { lastUserFocusedRef.current = e.currentTarget }}
                                onChange={(e) => onDescriptionChange(e.target.value)}
                                placeholder="Enter detailed description of this data object"
                                className="w-full px-3 py-2 lg:py-2 text-sm lg:text-base border-2 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-300 bg-white text-gray-900 placeholder:text-gray-400 border-red-300 hover:border-red-400 resize-vertical shadow-sm hover:shadow-md"
                                rows={2}
                                style={{ minHeight: '60px', maxHeight: '120px', overflowY: 'auto' }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Scrollable columns section */}
            <div className="flex-1 overflow-hidden min-h-0">
                {/* Column Configuration Header - minimalist */}
                <div className="mb-1 px-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm lg:text-base font-semibold text-gray-900">{t('Column Configuration')}</h3>
                        <span className="text-xs lg:text-sm text-gray-500">{analysis?.columns?.length ?? 0} {t('columns')}</span>
                    </div>
                </div>
                <div className="dd-columns" style={{ height: 'calc(100% - 28px)' }}>
                    <ColumnConfigurationTable
                        analysis={analysis}
                        onColumnUpdate={onColumnUpdate}
                        tableName={tableName}
                        dataObjectId={dataObjectId}
                        existingRules={existingRules}
                        rulesLoading={rulesLoading}
                        onSelectionChange={onSelectionChange}
                        initialSelectedColumns={initialSelectedColumns}
                        newColumnNames={newColumnNames}
                    />
                </div>
            </div>

            {/* Sticky footer */}
            {showFooter && (
                <div className="flex-shrink-0 px-6 py-0 border-t border-gray-200 bg-white">
                    <div className="flex justify-end gap-3 py-1">
                        {onReset && (
                            <button
                                type="button"
                                onClick={onReset}
                                disabled={isProcessing}
                                className="px-4 py-2 rounded-lg font-medium transition-all bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {resetLabel || t('Reset')}
                            </button>
                        )}
                        <button
                            type="button"
                            disabled={!isValid || isProcessing}
                            onClick={onSubmit}
                            className={!isValid || isProcessing ? 'px-4 py-2 rounded-lg font-medium transition-all bg-gray-300 text-gray-500 cursor-not-allowed' : getButtonClasses('primary')}
                            style={!isValid || isProcessing ? {} : getButtonStyles('primary')}
                        >
                            {isProcessing ? t('Processing...') : submitLabel}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataObjectConfig;